import React, { FC, useContext, useEffect, useState } from "react";
import CheckoutContext from "checkout";
import styled from "styled-components";

import Button from "./Button";
import { getUndef } from "checkout/utils";
import { GREEN, RED } from "../styles/constants";

const Arrow = (props) => (
  <svg
    width="20"
    height="18"
    viewBox="0 0 20 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M19.7468 8.32171L11.6518 0.303712C11.243 -0.101237 10.6297 -0.101237 10.2209 0.303712C9.81206 0.708661 9.81206 1.31609 10.2209 1.72103L16.5987 8.03825H1.02209C0.44972 8.03825 0 8.48369 0 9.05062C0 9.61755 0.44972 10.063 1.02209 10.063H16.6396L10.2618 16.2992C9.85295 16.7042 9.85295 17.3116 10.2618 17.7165C10.4662 17.919 10.7115 18 10.9568 18C11.2021 18 11.4883 17.919 11.6518 17.7165L19.7059 9.73903C19.9103 9.53656 19.9921 9.29359 19.9921 9.05062C20.033 8.72666 19.9103 8.48369 19.7468 8.32171Z"
      fill="#ffffff"
    />
  </svg>
);

const Close = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 47.971 47.971"
    {...props}
  >
    <path
      fill="#ffffff"
      d="M28.228 23.986L47.092 5.122a2.998 2.998 0 000-4.242 2.998 2.998 0 00-4.242 0L23.986 19.744 5.121.88a2.998 2.998 0 00-4.242 0 2.998 2.998 0 000 4.242l18.865 18.864L.879 42.85a2.998 2.998 0 104.242 4.241l18.865-18.864L42.85 47.091c.586.586 1.354.879 2.121.879s1.535-.293 2.121-.879a2.998 2.998 0 000-4.242L28.228 23.986z"
    />
  </svg>
);

const Exclamation = (props) => (
  <svg
    width="5"
    height="24"
    viewBox="0 0 5 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M3.82207 16.3524L4.71698 0H0.242432L1.05599 16.3524H3.82207Z"
      fill="#FF0000"
    />
    <path
      d="M2.44066 19.0374C1.70846 19.0374 1.05762 19.2814 0.650846 19.6882C0.244069 20.095 0 20.6645 0 21.478C0 22.2916 0.244069 22.8611 0.650846 23.3492C1.05762 23.756 1.62711 24 2.44066 24C3.17286 24 3.74235 23.756 4.14913 23.3492C4.5559 22.9424 4.79997 22.2916 4.79997 21.5594C4.79997 20.8272 4.5559 20.1763 4.14913 19.7696C3.82371 19.2814 3.17286 19.0374 2.44066 19.0374Z"
      fill="#FF0000"
    />
  </svg>
);

const Promocode: FC = (props) => {
  const { checkout, updateCheckoutAndSave, promocodeError } =
    useContext(CheckoutContext);
  const activePromocode = getUndef(
    () => checkout.discounts[0].discountApplication.code
  );

  const [value, setValue] = useState(activePromocode || "");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (promocodeError) {
      switch (promocodeError) {
        case "inactive":
          setErrorMessage("Купон не активен");
          break;
        case "unknown":
          setErrorMessage("Неизвестный тип купона");
          break;
        case "auth_only":
          setErrorMessage(
            "Акция доступна только авторизированным пользователям"
          );
          break;
        case "app_only":
          setErrorMessage("Акция доступна только в мобильном приложении");
          break;
        case "app_first":
          setErrorMessage(
            "Акция доступна только для первого заказа в мобильном приложении"
          );
          break;
        default:
          setErrorMessage("Промокод не найден. Попробуйте другой");
          break;
      }
    }
  }, [promocodeError]);

  const removePromocode = async () => {
    if (!activePromocode) {
      return;
    }

    const result = await updateCheckoutAndSave({
      discounts: {
        disconnect: [
          {
            code: activePromocode,
          },
        ],
      },
    });

    if (result) {
      setValue("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!value || value === activePromocode) return;

    try {
      const result = await updateCheckoutAndSave({
        discounts: {
          connect: [
            {
              code: value,
            },
          ],
        },
      });

      if (!result) {
        setError(true);
      }
    } catch (error) {
      console.error(error);
      setError(true);
    }
  };

  const active = !!activePromocode && value === activePromocode;
  const showSuccess = !!activePromocode && !error;

  return (
    <>
      <Form {...props} onSubmit={handleSubmit}>
        <Input
          active={active}
          value={value}
          placeholder="Введите промокод"
          onChange={(e) => {
            setError(false);
            setErrorMessage("");
            setValue(e.target.value);
          }}
        />

        {active ? (
          <Btn type="button" onClick={removePromocode} active>
            <StyledClose />
          </Btn>
        ) : (
          <Btn
            active={!active}
            disabled={!value.length || active}
            type="submit"
          >
            <StyledArrow />
          </Btn>
        )}
      </Form>

      {showSuccess && (
        <>
          <SuccessMessage>
            Промокод {activePromocode} активирован
          </SuccessMessage>

          <WarningWrapper>
            <WarningMessage>
              <Exclamation />
              <p>
                Скидки по&nbsp;промокодам <br /> и&nbsp;акции
                не&nbsp;суммируются
              </p>
            </WarningMessage>
          </WarningWrapper>
        </>
      )}

      {error && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </>
  );
};

const Message = styled.p`
  margin: 0.5em 0 0;
  font-size: 12px;
  text-align: center;
`;

const SuccessMessage = styled(Message)`
  color: ${GREEN};
`;

const ErrorMessage = styled(Message)`
  color: ${RED};
`;

const Form = styled.form`
  position: relative;
`;

interface InputProps {
  active: boolean;
}
const Input = styled.input<InputProps>`
  display: block;
  width: 100%;
  height: 50px;
  padding: 12px 20px;
  padding-right: 60px;
  border: none;
  background: ${({ active }) => (active ? GREEN : "#fff")};
  color: ${({ active }) => (active ? "#fff" : undefined)};
  border-radius: 10px;
`;

const StyledArrow = styled(Arrow)`
  path {
    fill: #fff;
    transition: 0.2s ease-in-out;
  }
`;

const StyledClose = styled(Close)`
  width: 16px;
  height: 16px;
`;

const Btn = styled(Button)<InputProps>`
  position: absolute;
  right: 4px;
  top: 4px;
  bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  min-width: 42px;
  min-height: 42px;
  background-color: ${GREEN};
  box-shadow: none !important;

  &:disabled {
    background-color: transparent;

    ${StyledArrow} path {
      fill: rgba(0, 0, 0, 0.25);
    }
  }
`;

const WarningMessage = styled.div`
  display: flex;
  align-items: center;
  max-width: 280px;
  font-size: 13px;
  line-height: 16px;
  color: #ff0000;

  svg {
    flex: 1;
  }

  p {
    margin: 0;
    flex-basis: 198px;
  }

  @media (max-width: 980px) {
    margin: 0 auto;
  }
`;

const WarningWrapper = styled.div`
  padding: 0.75rem 1rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  margin: 1rem auto 0;

  @media (max-width: 980px) {
    max-width: 100%;
  }

  @media (max-width: 600px) {
    margin: 1rem 1rem 0;
  }
`;

export default Promocode;
